import type { CSSProperties } from 'react'
import { designTokens } from '@/shared/lib/design-tokens'
import { cn } from '@/shared/lib'
import arrowAsset from '@/shared/assets/registration/rigt-bottom-arrow.svg'

export type AuthBadgeLayout = {
  x: string
  y: string
  width: string
  height: string
  pillX: string
  pillY: string
  arrowX: string
  arrowY: string
  arrowWidth: string
  arrowHeight: string
  arrowRotation: string
  arrowFlipY: number
}

export type AuthBadgeTextVariant = 'desktopLg' | 'desktopSm' | 'mobileBase' | 'mobileWide'
export type AuthBadgeSurfaceVariant = 'desktop' | 'mobile'

export type AuthBadgeProps = {
  label: string
  layout: AuthBadgeLayout
  textVariant: AuthBadgeTextVariant
  surfaceVariant?: AuthBadgeSurfaceVariant
  className?: string
}

const typographyStyle = (token: { size: string; lineHeight: string; weight: number }) => ({
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: token.size,
  lineHeight: token.lineHeight,
  fontWeight: token.weight,
})

export function AuthBadge({
  label,
  layout,
  textVariant,
  surfaceVariant = 'desktop',
  className,
}: AuthBadgeProps) {
  const showArrow = true
  const flipY = Number(layout.arrowFlipY) === 1
  const badgeTokens = designTokens.components.authBadge
  const textToken = (() => {
    if (textVariant === 'desktopLg') {
      return designTokens.typography.authBadge.lg
    }
    if (textVariant === 'mobileBase') {
      return designTokens.typography.authBadgeMobile.base
    }
    if (textVariant === 'mobileWide') {
      return designTokens.typography.authBadgeMobile.wide
    }
    return designTokens.typography.authBadge.sm
  })()
  const radius = surfaceVariant === 'mobile' ? badgeTokens.mobileRadius : badgeTokens.radius

  // Gradient matching the page background (pink to purple/orange)
  const backgroundGradient = badgeTokens.textGradient

  const wrapperStyle: CSSProperties = {
    left: layout.x,
    top: layout.y,
    width: layout.width,
    height: layout.height,
    position: 'absolute',
    filter: designTokens.effects.dropShadow.authBadge,
    WebkitFilter: designTokens.effects.dropShadow.authBadge,
  }

  const pillStyle: CSSProperties = {
    left: layout.pillX,
    top: layout.pillY,
    width: layout.width,
    height: layout.height,
    background: badgeTokens.background,
    borderRadius: radius,
    mixBlendMode: badgeTokens.mixBlendMode as CSSProperties['mixBlendMode'],
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    ...typographyStyle(textToken),
  }

  // Knockout text effect: text acts as a mask revealing the background gradient
  const textStyle: CSSProperties = {
    background: backgroundGradient,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const arrowStyle: CSSProperties = {
    left: layout.arrowX,
    top: layout.arrowY,
    width: layout.arrowWidth,
    height: layout.arrowHeight,
    position: 'absolute',
    transform: `rotate(${layout.arrowRotation}) scaleY(${flipY ? -1 : 1})`,
    transformOrigin: 'center',
  }

  return (
    <div className={cn('pointer-events-none', className)} style={wrapperStyle}>
      <div style={pillStyle}>
        <span style={textStyle}>{label}</span>
      </div>
      {showArrow ? <img src={arrowAsset} alt="" aria-hidden="true" style={arrowStyle} /> : null}
    </div>
  )
}
